import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Heart, Clock, ArrowRight, Construction } from 'lucide-react';
import { PageTransition, FadeInItem } from '@/components/motion/Animations';

const PROGRAMS = [
  {
    id: '90-dias',
    title: 'Programa 90 Dias',
    subtitle: 'Recuperação individual — Método Fundador',
    icon: Calendar,
    color: 'text-blue-500',
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    border: 'border-blue-200 dark:border-blue-800/40',
    available: true,
    route: '/programa/90-dias',
  },
  {
    id: '180-dias',
    title: 'Programa 180 Dias',
    subtitle: 'Recuperação completa — Premium+',
    icon: Clock,
    color: 'text-emerald-500',
    bg: 'bg-emerald-100 dark:bg-emerald-900/30',
    border: 'border-emerald-200 dark:border-emerald-800/40',
    available: true,
    route: '/programa/180-dias',
  },
  {
    id: 'familiar',
    title: 'Programa para Familiares',
    subtitle: 'Plano de 90 Dias',
    icon: Heart,
    color: 'text-rose-500',
    bg: 'bg-rose-100 dark:bg-rose-900/30',
    border: 'border-rose-200 dark:border-rose-800/40',
    available: true,
    route: '/curso/familiar',
  },
];

export default function ProgramasPage() {
  const navigate = useNavigate();

  return (
    <PageTransition>
      <div className="max-w-3xl mx-auto" data-testid="programas-page">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold mb-1">Programas de Recuperação</h1>
          <p className="text-sm text-muted-foreground">Trilhas estruturadas para a sua jornada</p>
        </div>

        {/* Program Cards */}
        <div className="space-y-3">
          {PROGRAMS.map((prog, i) => {
            const Icon = prog.icon;
            return (
              <FadeInItem key={prog.id} index={i}>
                <Card
                  className={`rounded-2xl border shadow-sm transition-all ${prog.border} ${prog.available ? 'hover:shadow-md cursor-pointer' : 'opacity-80'}`}
                  onClick={prog.available ? () => navigate(prog.route) : undefined}
                  data-testid={`program-card-${prog.id}`}
                >
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl ${prog.bg} flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`h-6 w-6 ${prog.color}`} strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <h3 className="font-bold text-sm">{prog.title}</h3>
                        {prog.available ? (
                          <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-[10px]">Disponível</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px] gap-1 text-muted-foreground">
                            <Construction className="h-2.5 w-2.5" /> Em preparação
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">{prog.subtitle}</p>
                    </div>
                    {prog.available ? (
                      <Button size="sm" className="rounded-xl" data-testid={`access-${prog.id}`}>
                        Acessar <ArrowRight className="h-3.5 w-3.5 ml-1" />
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" className="rounded-xl" disabled data-testid={`disabled-${prog.id}`}>
                        Em breve
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </FadeInItem>
            );
          })}
        </div>

        {/* Info */}
        <p className="text-center text-xs text-muted-foreground/50 mt-8">
          Conteúdo baseado no Método PAREI-AD e documentos oficiais da U-Clean.
        </p>
      </div>
    </PageTransition>
  );
}
